// src/components/blocks/BlockRenderer.js
// 页面骨架区块渲染器：把配置化的区块描述渲染为统一风格的界面。
// 支持类型：kpi / table / chart / info / stat / cards / list / tree / tabs / row / form / note
import React from 'react';
import { Table, Tag, Tree, Switch, Select, DatePicker, Popover, Tooltip as AntTooltip, Button, Input, InputNumber, message } from 'antd';
import ScrollTabs from '../ScrollTabs';
import { PlusOutlined, EyeOutlined, EyeInvisibleOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  ResponsiveContainer, LineChart, BarChart, AreaChart, ComposedChart, PieChart,
  Line, Bar, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Pie, Cell, ReferenceLine,
} from 'recharts';
import ChinaSiteMap from '../ChinaSiteMap';

const tick = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const tooltipStyle = { background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff' };
const PALETTE = ['#00d4ff', '#52e8b0', '#f4c542', '#3a8dde', '#8b7cf6', '#ff5a7f'];

// 配置化列 -> antd 列（支持 children 多级表头，如落后电池报表按电池簇分组多列）
const mapColumn = (c, i, onAction) => {
  const col = {
    title: c.title,
    dataIndex: c.dataIndex,
    key: c.key || c.dataIndex || i,
    width: c.width,
    ellipsis: c.ellipsis,
  };
  if (c.children && c.children.length) {
    col.children = c.children.map((ch, j) => mapColumn(ch, `${col.key}-${j}`, onAction));
    return col;
  }
  if (c.rowSpanKey) {
    // 首列「日期」按日期合并：同一日期仅首行显示，其余行 rowSpan=0
    col.render = (v, r) => ({ children: v, props: { rowSpan: (r && r[c.rowSpanKey]) ?? 1 } });
  } else if (c.rowAction) {
    // 评审 56(用户 41)：行内操作按钮（「进入监控」）直接绑定该行站点，
    // 与站点名称同格呈现（而非在搜索栏旁边或单独放最右侧一列），点击进入对应单站监控
    col.render = (v, r) => (
      <span className="blk-name-cell">
        <span className="blk-name-text">{v ?? r.name ?? ''}</span>
        <Button
          type="primary" ghost size="small" className="blk-row-btn"
          onClick={() => onAction({ key: c.actionKey || 'enterStation', id: r.key ?? r.id, name: r.name })}
        >
          {c.actionLabel || '进入监控'}
        </Button>
      </span>
    );
  } else if (c.expandKey) {
    // 可展开明细列（如「PCS 工作状态」点击加号查看哪些运行 / 哪些停机）
    col.render = (v, r) => {
      const list = (r && r[c.expandKey]) || [];
      const dash = <span style={{ color: 'rgba(255,255,255,0.3)' }}>-</span>;
      if (!list.length) return dash;
      const content = (
        <div className="blk-expand-list">
          <div className="blk-expand-title">{c.expandTitle || '明细'}</div>
          {list.map((it) => (
            <div className="blk-expand-row" key={it.name}>
              <span className={`dot ${it.running ? 'online' : 'offline'}`} />
              <span className="blk-expand-name">{it.name}</span>
              <span className="blk-expand-state">{it.running ? '运行' : '停机'}</span>
            </div>
          ))}
        </div>
      );
      return (
        <span className="blk-expand-cell">
          <span>{v}</span>
          <Popover content={content} title={null} trigger="click" placement="right" overlayClassName="blk-expand-pop">
            <button type="button" className="blk-expand-btn" aria-label="展开明细">
              <PlusOutlined />
            </button>
          </Popover>
        </span>
      );
    };
  } else if (c.suffix) {
    col.render = (v) => (v === undefined || v === null || v === '-' || v === ''
      ? <span style={{ color: 'rgba(255,255,255,0.3)' }}>-</span>
      : <span>{String(v).split('/').map((p) => `${p.trim()}${c.suffix}`).join(' / ')}</span>);
  } else if (c.tagMap) {
    col.render = (v) => (v || v === 0
      ? <Tag color={c.tagMap[v] || 'default'} style={{ margin: 0 }}>{v}</Tag>
      : <span style={{ color: 'rgba(255,255,255,0.3)' }}>-</span>);
  } else if (c.dot) {
    col.render = (v) => (
      <span><span className={`dot ${(c.dotMap && c.dotMap[v]) || 'online'}`} />{(c.textMap && c.textMap[v]) || v}</span>
    );
  } else if (c.num) {
    col.render = (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{typeof v === 'number' ? v.toLocaleString() : v}</span>;
  } else if (c.accent) {
    col.render = (v) => <span style={{ color: '#00d4ff' }}>{v}</span>;
  } else if (c.strong) {
    col.render = (v) => <span style={{ color: '#fff', fontWeight: 500 }}>{v}</span>;
  }
  return col;
};

const buildColumns = (cols = [], onAction) => cols.map((c, i) => mapColumn(c, i, onAction));

const Panel = ({ title, children, className }) => (
  <div className={`panel${className ? ` ${className}` : ''}`}>
    {title ? <div className="panel-title">{title}</div> : null}
    {children}
  </div>
);

// 图卡片内的筛选控件（缺陷 92 / 100 / 108：筛选需放在图 / 卡片内部、内容上方，而非页面头部）
const CONTROL_LABEL_STYLE = { color: 'rgba(255,255,255,0.55)', fontSize: 12 };
const ControlRow = ({ controls, values, onChange }) => (
  <div className="blk-controls">
    {(controls || []).map((c) => {
      // 按钮控件（查询 / 重置 / 导出 / 评价标准差等）
      if (c.type === 'button') {
        return (
          <Button
            key={c.key}
            size="small"
            type={c.primary ? 'primary' : 'default'}
            onClick={() => message.success(c.tip || c.label)}
          >
            {c.label}
          </Button>
        );
      }
      return (
        <span key={c.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {c.label ? <span style={CONTROL_LABEL_STYLE}>{c.label}</span> : null}
          {c.type === 'daterange' ? (
            <DatePicker.RangePicker
              size="small"
              value={values[c.key]}
              onChange={(v) => onChange(c.key, v)}
              format="YYYY-MM-DD"
              style={{ width: c.width || 230 }}
            />
          ) : (
            <Select
              size="small"
              value={values[c.key]}
              onChange={(v) => onChange(c.key, v)}
              options={c.options || []}
              style={{ minWidth: c.width || 120 }}
            />
          )}
        </span>
      );
    })}
  </div>
);

const ChartView = ({ b }) => {
  const [vals, setVals] = React.useState(() => (b.controls || []).reduce((a, c) => { a[c.key] = c.defaultValue; return a; }, {}));
  const setVal = (k, v) => setVals((s) => ({ ...s, [k]: v }));
  const controls = b.controls && b.controls.length
    ? <ControlRow controls={b.controls} values={vals} onChange={setVal} /> : null;

  if (b.chartType === 'pie') {
    const nameKey = b.nameKey || 'name';
    const valueKey = b.valueKey || 'value';
    return (
      <Panel title={b.title} extra={b.extra}>
        {controls}
        <ResponsiveContainer width="100%" height={b.height || 260}>
          <PieChart>
            <Tooltip contentStyle={tooltipStyle} />
            {b.legend === false ? null : (
              <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }} iconSize={8} />
            )}
            <Pie
              data={b.data} dataKey={valueKey} nameKey={nameKey}
              outerRadius={b.outerRadius || 92} innerRadius={b.innerRadius || 46}
              paddingAngle={2} label={false}
            >
              {(b.data || []).map((d, i) => (
                <Cell key={i} fill={(b.colors && b.colors[i]) || PALETTE[i % PALETTE.length]} stroke="rgba(0,0,0,0.2)" />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </Panel>
    );
  }

  const series = (b.series || []).map((s, i) => ({ ...s, color: s.color || PALETTE[i % PALETTE.length] }));
  const mixed = b.chartType === 'composed' || series.some((s) => s.area);
  // 用户 42：同一图内量纲差异大的曲线（如 电量 kWh 与 系统效率 %）分左右双轴，避免小量纲曲线被压成直线
  const hasRight = series.some((s) => s.axis === 'right');
  const Chart = b.chartType === 'bar' ? BarChart
    : b.chartType === 'area' ? AreaChart
      : mixed ? ComposedChart : LineChart;

  return (
    <Panel title={b.title} extra={b.extra}>
      {controls}
      <ResponsiveContainer width="100%" height={b.height || 260}>
        <Chart data={b.data} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
          <XAxis dataKey={b.xKey || 'clock'} tick={tick} axisLine={false} tickLine={false} interval={b.interval} />
          {hasRight
            ? <YAxis yAxisId="left" tick={tick} axisLine={false} tickLine={false} width={52} domain={b.yDomain} />
            : <YAxis tick={tick} axisLine={false} tickLine={false} width={52} domain={b.yDomain} />}
          {hasRight
            ? <YAxis yAxisId="right" orientation="right" tick={tick} axisLine={false} tickLine={false} width={46} domain={['auto', 'auto']} />
            : null}
          <Tooltip contentStyle={tooltipStyle} />
          {b.legend === false ? null : (
            <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }} iconSize={8} />
          )}
          {b.zeroLine ? <ReferenceLine y={0} stroke="rgba(255,255,255,0.35)" /> : null}
          {series.map((s) => {
            const kind = s.type || (b.chartType === 'bar' ? 'bar' : b.chartType === 'area' ? 'area' : s.area ? 'area' : 'line');
            // 评审 61：禁止样条过冲；series.curve 可指定 stepAfter 等，缺省 linear
            const curve = s.curve || 'linear';
            const axisId = hasRight ? (s.axis === 'right' ? 'right' : 'left') : undefined;
            if (kind === 'area') {
              return <Area key={s.key} yAxisId={axisId} type={curve} dataKey={s.key} name={s.name} stroke={s.color} fill={s.color} fillOpacity={0.16} strokeWidth={2} baseValue={0} />;
            }
            if (kind === 'bar') {
              return <Bar key={s.key} yAxisId={axisId} dataKey={s.key} name={s.name} fill={s.color} radius={[4, 4, 0, 0]} barSize={b.barSize || 16} />;
            }
            return (
              <Line
                key={s.key}
                yAxisId={axisId}
                type={curve}
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                dot={false}
                strokeWidth={2}
                strokeDasharray={s.dashed ? '5 4' : undefined}
              />
            );
          })}
        </Chart>
      </ResponsiveContainer>
    </Panel>
  );
};

// 缺陷 125：图表区块用 React.memo 包装——同一份 block 引用（数据未变化）时跳过重绘，
// 避免「实时运行」等页面每 5 秒定时刷新时把小时级图表整体重绘一遍。
const ChartBlock = React.memo(ChartView, (prev, next) => prev.b === next.b);

const KpiRow = ({ items = [] }) => (
  <div className="kpi-grid kpi-grid-auto" style={items.length <= 3 ? { gridTemplateColumns: `repeat(${items.length}, 1fr)` } : undefined}>
    {items.map((k) => (
      <div className="kpi-card" key={k.label}>
        {k.icon ? <div className="kpi-icon">{k.icon}</div> : null}
        <div className="kpi-label">{k.label}</div>
        <div className="kpi-value">{k.value}{k.unit ? <span className="unit">{k.unit}</span> : null}</div>
        {k.foot ? <div className={`kpi-foot ${k.up ? 'up' : ''} ${k.down ? 'down' : ''}`}>{k.foot}</div> : null}
      </div>
    ))}
  </div>
);

const InfoBlock = ({ b }) => (
  <Panel title={b.title} extra={b.extra}>
    <div className="dev-kv" style={b.cols === 1 ? { gridTemplateColumns: '1fr' } : undefined}>
      {(b.items || []).map(([k, v]) => (
        <div className="dev-kv-row" key={k}>
          <span className="dev-kv-k">{k}</span>
          <span className="dev-kv-v">{v}</span>
        </div>
      ))}
    </div>
  </Panel>
);

const StatBlock = ({ b }) => (
  <Panel title={b.title} extra={b.extra}>
    <div className="strat-grid">
      {(b.items || []).map((s) => (
        <div className="strat-item" key={s.label}>
          <div className="strat-item-label">{s.label}</div>
          <div className="strat-item-value">{s.value}{s.unit ? <span className="unit">{s.unit}</span> : null}</div>
        </div>
      ))}
    </div>
  </Panel>
);

// 图片区块（doc01 2.3 p10：单站概览卡片栏展示电站图片）
// 缺陷 86：卡片栏中的电站图片需受约束（缩略图尺寸），而非整页一张巨图
// 未配置图片地址时使用空白占位（接口返回电站图片前不展示任何示例图/英文描述）
const ImageBlock = ({ b }) => (
  <Panel title={b.title} extra={b.extra}>
    {b.src ? (
      <img
        src={b.src}
        alt={b.alt || b.title || '图片'}
        style={{ display: 'block', width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 10 }}
      />
    ) : (
      <div
        style={{
          width: '100%',
          height: 200,
          borderRadius: 10,
          background: 'rgba(255,255,255,0.04)',
          border: '1px dashed rgba(255,255,255,0.12)',
        }}
      />
    )}
    {b.caption ? (
      <div style={{ marginTop: 8, color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>{b.caption}</div>
    ) : null}
  </Panel>
);

const CardBlock = ({ b }) => (
  <Panel title={b.title} extra={b.extra}>
    <div className="dev-grid">
      {(b.items || []).map((c) => (
        <div className={`dev-card${c.fault ? ' fault' : ''}`} key={c.name}>
          <div className="dev-card-head">
            <span className="dev-name">{c.name}</span>
            {c.state ? <span className="dev-status"><span className={`dot ${c.dot || 'online'}`} />{c.state}</span> : null}
          </div>
          {c.tags && c.tags.length ? (
            <div className="dev-tags">{c.tags.map((t) => <Tag key={t} style={{ margin: 0 }}>{t}</Tag>)}</div>
          ) : null}
          <div className="dev-kv">
            {(c.kvs || []).map(([k, v]) => (
              <div className="dev-kv-row" key={k}>
                <span className="dev-kv-k">{k}</span>
                <span className="dev-kv-v">{v}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </Panel>
);

const ListBlock = ({ b }) => (
  <Panel title={b.title} extra={b.extra}>
    {(b.items || []).map((it, i) => (
      <div className="blk-list-row" key={it.title || i}>
        {it.tag ? <Tag color={it.color || 'default'} style={{ margin: 0 }}>{it.tag}</Tag> : null}
        <span className="blk-list-title">{it.title}</span>
        {it.desc ? <span className="blk-list-desc">{it.desc}</span> : null}
        {it.time ? <span className="blk-list-time">{it.time}</span> : null}
      </div>
    ))}
  </Panel>
);

const TreeBlock = ({ b }) => (
  <Panel title={b.title} extra={b.extra}>
    <div className="block-tree">
      <Tree
        checkable={b.checkable}
        defaultExpandAll
        defaultCheckedKeys={b.checkedKeys}
        showLine={{ showLeafIcon: false }}
        treeData={b.treeData}
        selectable={false}
      />
    </div>
  </Panel>
);

// 表单区块：默认只读展示；b.editable 为真时价格类字段可编辑（含时间段），支持统一下发
const FormBlock = ({ b }) => {
  const fields = b.fields || [];
  const [vals, setVals] = React.useState(() => fields.reduce((a, f) => {
    a[f.label] = f.value;
    if (f.time != null) a[`${f.label}-time`] = f.time;
    return a;
  }, {}));
  const setVal = (key, v) => setVals((s) => ({ ...s, [key]: v }));
  return (
    <Panel title={b.title} extra={b.extra}>
      <div className="form-grid" style={b.cols === 1 ? { gridTemplateColumns: '1fr' } : undefined}>
        {fields.map((f) => (
          <div className="form-item" key={f.label}>
            <span className="form-item-label">{f.label}</span>
            <span className="form-item-value">
              {f.type === 'switch' ? <Switch size="small" disabled checked={!!f.value} />
                : (b.editable && f.input === 'price' ? (
                  <span className="form-edit">
                    <InputNumber size="small" min={0} step={0.01} value={vals[f.label]} onChange={(v) => setVal(f.label, v)} />
                    <em>元/kWh</em>
                    <Input size="small" value={vals[`${f.label}-time`]} onChange={(e) => setVal(`${f.label}-time`, e.target.value)} />
                  </span>
                ) : (f.value || '-'))}
            </span>
          </div>
        ))}
      </div>
      {b.editable ? (
        <div className="form-edit-actions">
          <Button type="primary" size="small" onClick={() => message.success('电价已下发至选中站点，需二次口令校验')}>下发配置</Button>
        </div>
      ) : null}
    </Panel>
  );
};

const NoteBlock = ({ b }) => (
  <div className="blk-note">
    <div className="blk-note-text">{b.text}</div>
    {b.tag ? <div className="blk-note-tag">{b.tag}</div> : null}
  </div>
);

// 模块分区标题（doc：概览类页面按模块分区，而非按图表分区）
// 带 timeFilter 的分区顶部提供「时间范围」控件，可逐模块单独设置（含自定义）
const TIME_PRESETS = ['近7日', '当月', '当年', '全部', '自定义'].map((v) => ({ value: v, label: v }));

const SectionBlock = ({ b }) => {
  const custom = b.timeFilter && b.timeValue
    && !TIME_PRESETS.some((p) => p.value === b.timeValue);
  const selectVal = custom ? '自定义' : (b.timeValue || '近7日');
  return (
    <div className="mod-section">
      <div className="mod-section-head">
        <span className="mod-section-title">{b.title}</span>
        {b.timeFilter && b.onTimeChange ? (
          <span className="mod-section-time">
            <span className="mod-section-time-label">时间范围</span>
            <Select
              size="small"
              value={selectVal}
              options={TIME_PRESETS}
              className="mod-time-select"
              onChange={(v) => b.onTimeChange(v === '自定义' ? '自定义' : v)}
            />
            {selectVal === '自定义' ? (
              <DatePicker.RangePicker
                size="small"
                className="mod-time-picker"
                popupClassName="mod-time-pop"
                onChange={(d) => b.onTimeChange(d && d[0] ? `${d[0].format('YYYY-MM-DD')} ~ ${d[1].format('YYYY-MM-DD')}` : '自定义')}
              />
            ) : null}
          </span>
        ) : null}
      </div>
    </div>
  );
};

// 地图区块（doc01 2.1：数据大屏内嵌站点地理分布，复用站点地图的官方标准底图）
const MAP_STATE_COLOR = { online: '#52e8b0', delay: '#f4c542', offline: '#ff5a7f' };
const MAP_STATE_TEXT = { online: '在线', delay: '通信延迟', offline: '离线' };

const MapBlock = ({ b, onAction }) => {
  const all = b.points || [];
  const hiddenIds = Array.isArray(b.hidden) ? b.hidden : [];
  const visible = all.filter((p) => !hiddenIds.includes(p.key));
  return (
    <Panel title={b.title} extra={b.extra}>
      <div className="blk-map-layout">
        {/* 左：官方中国 / 世界标准底图（含审图号合规署名，可在图内切换） */}
        <div className="blk-map-canvas">
          <ChinaSiteMap sites={visible} defaultMode={b.mode || 'china'} />
        </div>
        {/* 右：站点列表与隐藏站点入口（隐藏后不计入统计） */}
        <div className="blk-map-side">
          <div className="blk-map-side-head">
            <span>站点列表</span>
            <small>眼睛图标可隐藏站点（不计入统计）</small>
          </div>
          <div className="blk-map-list">
            {all.map((m) => {
              const hidden = hiddenIds.includes(m.key);
              const color = MAP_STATE_COLOR[m.state] || MAP_STATE_COLOR.online;
              return (
                <div key={m.key} className={`sitemap-list-item${hidden ? ' hidden' : ''}`}>
                  <span className="dot" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />
                  <span className="sitemap-list-name">{m.name}</span>
                  <span className="sitemap-list-meta">{m.typeLabel}</span>
                  <AntTooltip title={hidden ? '取消隐藏' : '隐藏站点（不计入统计）'}>
                    <Button
                      type="text"
                      size="small"
                      className="sitemap-hide-btn"
                      icon={hidden ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      onClick={() => onAction({ key: 'hideSite', id: m.key })}
                    />
                  </AntTooltip>
                </div>
              );
            })}
          </div>
          <Button
            block
            icon={<ReloadOutlined />}
            onClick={() => onAction({ key: 'resetHidden' })}
          >
            恢复全部显示
          </Button>
          <div className="blk-map-legend">
            {Object.keys(MAP_STATE_TEXT).map((k) => (
              <span key={k} className="blk-map-legend-item">
                <span className="dot" style={{ background: MAP_STATE_COLOR[k] }} />{MAP_STATE_TEXT[k]}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Panel>
  );
};

// 表格卡片（缺陷 92 / 100：筛选控件放在卡片内、表格上方）
const TableBlock = ({ b, onAction }) => {
  const [vals, setVals] = React.useState(() => (b.controls || []).reduce((a, c) => { a[c.key] = c.defaultValue; return a; }, {}));
  const setVal = (k, v) => setVals((s) => ({ ...s, [k]: v }));
  const controls = b.controls && b.controls.length
    ? <ControlRow controls={b.controls} values={vals} onChange={setVal} /> : null;
  return (
    <Panel title={b.title} extra={b.extra}>
      {controls}
      {b.note ? <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, margin: '0 0 10px' }}>{b.note}</div> : null}
      <Table
        rowKey={(r) => r.key ?? r.id ?? r.name}
        dataSource={b.rows}
        columns={buildColumns(b.columns, onAction)}
        rowSelection={b.selectable ? { defaultSelectedRowKeys: b.selectedKeys || [] } : undefined}
        pagination={b.page ? { pageSize: b.page, size: 'small' } : false}
        size="middle"
        scroll={{ x: b.scrollX || 'max-content' }}
      />
    </Panel>
  );
};

const renderBlock = (b, onAction) => {
  if (!b) return null;
  switch (b.type) {
    case 'section': return <SectionBlock b={b} />;
    case 'map': return <MapBlock b={b} onAction={onAction} />;
    case 'kpi': return <KpiRow items={b.items} />;
    case 'info': return <InfoBlock b={b} />;
    case 'stat': return <StatBlock b={b} />;
    case 'image': return <ImageBlock b={b} />;
    case 'cards': return <CardBlock b={b} />;
    case 'list': return <ListBlock b={b} />;
    case 'tree': return <TreeBlock b={b} />;
    case 'form': return <FormBlock b={b} />;
    case 'note': return <NoteBlock b={b} />;
    case 'chart': return <ChartBlock b={b} />;
    case 'table': return <TableBlock b={b} onAction={onAction} />;
    case 'tabs':
      return (
        <Panel className="blk-tabs">
          <ScrollTabs
            items={(b.items || []).map((it) => ({
              key: it.key,
              label: it.label,
              children: (
                <div className="blk-tabs-body">
                  {(it.blocks || []).map((sub, i) => <BlockRenderer block={sub} onAction={onAction} key={i} />)}
                </div>
              ),
            }))}
          />
        </Panel>
      );
    case 'row':
      return (
        <div className={b.layout || 'grid-2'} style={b.style}>
          {(b.blocks || []).map((sub, i) => <BlockRenderer block={sub} onAction={onAction} key={i} />)}
        </div>
      );
    default:
      return null;
  }
};

const BlockRenderer = ({ block, onAction }) => <>{renderBlock(block, onAction)}</>;

export default BlockRenderer;